import React, { useRef, useState, useCallback, useEffect } from "react";
import styles from "./E3FileUpDown.module.css";
import { FileDrop } from "react-file-drop";
import E3Button from "./E3Button";
import PropTypes from "prop-types";
import { E3WijmoGrid } from "../DataGrid";
import { FlexContainer, FlexItemContainer } from "../PageLayout";
import useFetch from "e3-common/hooks/useFetch";
import { PropConst } from "e3-common/constants/common";
import { e3 } from "e3-common/utils/Utils";
import { omit } from "lodash";

/**
 * 단일 API 거래 함수
 * @param {{isReadonly:boolean, isShowDelBtn:boolean, isShowAddBtn: boolean,  }} props
 * @returns
 */
const E3FileUpDown = React.forwardRef((props, ref) => {
  const { request } = useFetch();
  const refState = useRef();
  const [columns, setColumns] = useState([]);
  const [isReadonly, setReadonly] = useState(props.isReadonly);
  const [isShowDelBtn, setShowDelBtn] = useState(props.isShowDelBtn);
  const [isShowAddBtn, setShowAddBtn] = useState(props.isShowAddBtn);

  const [currState, setCurrState] = useState({
    source: [],
    deleteSource: [],
  });

  const getColumns = () => {
    let cols = [
      { header: "JOB_TYPE", binding: "JOB_TYPE", visible: false },
      { header: "JOB_KEY", binding: "JOB_KEY", visible: false },
      { header: "ATTACH_TYPE", binding: "ATTACH_TYPE", visible: false },
      { header: "ATTACH_SEQ", binding: "ATTACH_SEQ", visible: false },
      { header: "NO", binding: "NO", width: 40, align: "center" },
      {
        header: "파일명",
        binding: "ORG_FILE_NM",
        align: "left",
        isLink: true,
        width: "*",
        minWidth: 200,
        clickEvent: async (e, ctx) => {
          // 파일 다운로드
          const row = ctx.item;
          if (row.inputFile) {
            const url = await e3.convertFileToBinaryString(row.inputFile);
            let a = document.createElement("a");
            a.download = row.inputFile.name;
            a.href = url;
            a.click();
          } else {
            const param = omit(row, ["inputFile"]);

            const response = await request(
              null,
              {
                ...param,
                serviceId: "htkFileService",
                process: [{ fileDownload: "파일다운로드" }],
              },
              { showLoad: false }
            );
            if (response) {
              let a = document.createElement("a");
              a.download = row.ORG_FILE_NM;
              a.href = response.data.FILE;
              a.click();
            }
          }
        },
      },
    ];

    if (!isReadonly && isShowDelBtn) {
      const col = {
        header: "삭제",
        binding: "BTN_DEL",
        width: 70,
        align: "center",
        isButton: true,
        isButtonColor: "colorNoBorder", // 테두리 없는 아이콘
        isButtonIcon: PropConst.iconType.btn_close,
        clickEvent: _onRemoveFile,
      };

      cols.push(col);
    }

    return cols;
  };

  /* #region useEffect 영역 */
  useEffect(() => {
    if (!props.jobType) {
      throw new Error(`E3FileUpDown: jobType 이 정의되지 않았습니다.`);
    } else if (!props.attachType) {
      throw new Error(`E3FileUpDown: attachType 이 정의되지 않았습니다.`);
    }
  }, []);

  useEffect(() => {
    controls.grid.ref.current?.setColumns(columns ?? []);
  }, [columns]);

  useEffect(() => {
    const tmpCols = getColumns();
    setColumns(tmpCols);
  }, [isReadonly]);

  useEffect(() => {
    const columns = getColumns();
    setColumns(columns);
  }, [isShowDelBtn]);

  useEffect(() => {
    controls.grid.ref.current?.setDataBinding(currState.source);
    refState.current = { ...refState.current, source: currState.source };
  }, [currState.source]);

  useEffect(() => {
    refState.current = {
      ...refState.current,
      deleteSource: currState.deleteSource,
    };
  }, [currState.source]);
  /* #endregion useEffect 영역  */

  /* #region useImperativeHandle 영역 */
  React.useImperativeHandle(ref, () => ({
    clearData: () => {
      setCurrState({ deleteSource: [], source: [] });
      controls.grid.ref.current.clearDataBinding();
    },
    /**
     * 저장된 파일 목록 조회
     */
    fileLoad: async (pJobKey) => {
      const param = {
        JOB_TYPE: props.jobType,
        ATTACH_TYPE: props.attachType,
        JOB_KEY: pJobKey,
      };

      const response = await request(
        null,
        {
          ...param,
          serviceId: "htkFileService",
          process: [{ selectFileListBy: "파일목록조회" }],
        },
        { showLoad: false }
      );

      if (response) {
        const files = response.data.result?.array || [];

        const data = [];
        files.forEach((file) => {
          let item = { ...file, id: e3.getUUID(), inputFile: null };
          data.push(item);
        });

        setCurrState({ ...currState, source: data, deleteSource: [] });
      }
    },
    /**
     * 파일 첨부 파라메터 반환
     */
    getUploadParam: async () => {
      const files = [];
      for (let i = 0; i < currState.source.length; i++) {
        const fileInfo = currState.source[i];
        if (fileInfo.inputFile) {
          const fileString = await e3.convertFileToBinaryString(
            fileInfo.inputFile
          );

          const file = {
            Name: fileInfo.inputFile.name,
            Size: fileInfo.inputFile.size,
            Data: fileString,
          };
          files.push(file);
        }
      }

      const param = {
        JOB_TYPE: props.jobType,
        ATTACH_TYPE: props.attachType,
        DELETE_FILES: currState.deleteSource,
        FILES: files,
      };

      return param;
    },
    /**
     * 그리드 삭제버튼 숨기기
     */
    hideDelBtn: () => {
      setShowDelBtn(false);
    },
    /**
     * 그리드 삭제버튼 표시
     */
    showDelBtn: () => {
      if (isReadonly) {
        return;
      }

      setShowDelBtn(true);
    },
    /**
     * 파일선택 버튼 숨기기
     */
    hideAddBtn: () => {
      setShowAddBtn(false);
    },
    /**
     * 파일선택 버튼 표시
     */
    showAddBtn: () => {
      setShowAddBtn(true);
    },
    /**
     * 읽기모드 적용
     */
    setReadonly: (value) => {
      setReadonly(value);
    },
  }));
  /* #endregion useImperativeHandle 영역 */

  /* #region 사용자 함수 영역 */

  // 신규파일 추가
  const _onAddFile = (files) => {
    if (!isShowAddBtn) {
      return;
    }

    const data = [...currState.source];
    Array.from(files).forEach((file, index) => {
      let item = {
        id: e3.getUUID(),
        JOB_TYPE: props.jobType,
        JOB_KEY: "",
        ATTACH_TYPE: props.attachType,
        ATTACH_SEQ: -1,
        FILE_NM: "",
        FILE_EXT: "",
        FILE_SIZE: "",
        FILE_PATH: "",
        ORG_FILE_NM: file.name,
        inputFile: file,
      };

      data.push(item);
    });

    setCurrState({ ...currState, source: data });

    if (controls.inputFile.ref.current.value !== null) {
      controls.inputFile.ref.current.value = "";
    }
  };

  // 목록에서 삭제, 저장시 삭제 대상 목록에 추가
  const _onRemoveFile = (e, ctx) => {
    const row = ctx.item;
    const tempSource = refState.current?.source.filter(
      (x) => x.id.toString() !== row.id
    );

    setCurrState({
      ...refState.current,
      source: tempSource,
      deleteSource: [
        ...refState.current.deleteSource,
        !row.inputFile && row,
      ].filter((e) => e),
    });
  };

  /* #endregion 사용자 함수 영역 */

  /* #region controls */
  const controls = {
    buttons: {
      btnSelectFile: {
        label: "파일선택",
        authBtn: "SEARCH",
        onClick: () => {
          controls.inputFile.ref.current?.click();
        },
      },
    },
    fileDrop: {
      onTargetClick: () => {
        //controls.inputFile.ref.current?.click();
      },
      onDrop: (files) => {
        _onAddFile(files);
      },
    },
    inputFile: {
      ref: useRef(),
      onChange: (e) => {
        _onAddFile(e.target.files);
      },
    },
    grid: {
      ref: useRef(),
      columns: getColumns(),
    },
  };
  /* #endregion controls*/

  /* #region ======== renderer 영역 ======== */
  return (
    <FlexItemContainer>
      {!isReadonly && isShowAddBtn && (
        <FileDrop
          onDrop={controls.fileDrop.onDrop}
          onTargetClick={controls.fileDrop.onTargetClick}
        >
          <div
            className={styles.dropContainer}
            style={{
              height: `${props.dropHeight}`,
            }}
          >
            <div className={styles.title}>
              <p>여기로 파일을 끌어와 주세요.</p>
            </div>
            <div className={styles.button}>
              <E3Button
                label={controls.buttons.btnSelectFile.label}
                colorType="sub4"
                iconType={"btn_add_w"}
                authBtn={controls.buttons.btnSelectFile.authBtn}
                checkAuth={true}
                onClick={controls.buttons.btnSelectFile.onClick}
              />
            </div>
          </div>
        </FileDrop>
      )}
      <div className={styles.gridContainer}>
        <E3WijmoGrid ref={controls.grid.ref} columns={controls.grid.columns} />
      </div>
      <input
        ref={controls.inputFile.ref}
        style={{ display: "none" }}
        multiple={true}
        id="contentList"
        type="file"
        accept={props.acceptValue}
        onChange={controls.inputFile.onChange}
      />
    </FlexItemContainer>
  );
  /* #endregion ======== renderer 영역 ======== */
});

/* #region ======== propTypes 영역 ======== */
E3FileUpDown.defaultProps = {
  width: "100%",
  height: "100%",
  dropHeight: "65px",
  isReadonly: false,
  isShowDelBtn: true,
  isShowAddBtn: true,
};

E3FileUpDown.propTypes = {
  jobType: PropTypes.string.isRequired,
  attachType: PropTypes.string.isRequired,
  width: PropTypes.string,
  height: PropTypes.string,
  dropHeight: PropTypes.string.isRequired,
  isReadonly: PropTypes.bool,
  isShowDelBtn: PropTypes.bool,
  isShowAddBtn: PropTypes.bool,
};
/* #endregion ======== propTypes 영역 ======== */

export default E3FileUpDown;
